import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from 'react';

export interface CartItemInput {
  id: number;
  name: string;
  price: number;
  veg?: boolean;
  image?: string;
  /** Special instruction for the kitchen (e.g. "less spicy, no onion"). */
  note?: string;
  /**
   * GST rate % for this line, as resolved by the server from the menu item.
   *
   * Per line because a bill can mix rates — cooked food at 5%, a packaged drink at 18% — and one
   * rate applied to the whole subtotal taxes both of them wrongly. Undefined falls back to the
   * outlet's rate, so a menu on a single rate behaves exactly as before.
   */
  gst?: number;
  /** Whether `price` already contains the GST. Undefined = follow the outlet's setting. */
  taxInclusive?: boolean;
}

export interface CartItem extends CartItemInput {
  qty: number;
}

interface CartContextValue {
  items: CartItem[];
  count: number;
  subTotal: number;
  qtyOf: (id: number) => number;
  add: (item: CartItemInput, qty?: number) => void;
  inc: (id: number) => void;
  dec: (id: number) => void;
  remove: (id: number) => void;
  clear: () => void;
  /** Right-side cart drawer visibility. */
  isOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  toggleCart: () => void;
}

const CartContext = createContext<CartContextValue | null>(null);

function storageKey(userId: string | number | null): string {
  return `thalios.cart.${userId ?? 'guest'}`;
}

/**
 * Fold the cart a GUEST was holding into the cart the customer already had, summing quantities
 * for a dish that appears in both.
 *
 * Exported because this rule is the whole first-order flow and it failed silently: a guest taps
 * ADD, the name+mobile sheet appears, the item is added the moment they sign in — and then the
 * identity switch replaced the cart with the (empty) stored one. The sheet said "Signed in", the
 * cart was empty, and "Place order" answered "Add items first".
 */
export function mergeGuestCart(guest: CartItem[], stored: CartItem[]): CartItem[] {
  const merged = stored.map((i) => ({ ...i }));
  for (const item of guest) {
    const existing = merged.find((m) => m.id === item.id);
    if (existing) existing.qty += item.qty;
    else merged.push({ ...item });
  }
  return merged;
}

function readCart(userId: string | number | null): CartItem[] {
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    return raw ? (JSON.parse(raw) as CartItem[]) : [];
  } catch {
    return [];
  }
}

export function CartProvider({
  children,
  userId = null
}: {
  children: ReactNode;
  userId?: string | number | null;
}) {
  // Lazy-init from the current user's cart so mount never writes an empty [] over it.
  const keyRef = useRef<string | number | null>(userId);
  const [items, setItems] = useState<CartItem[]>(() => readCart(userId));
  const [isOpen, setIsOpen] = useState(false);

  // Reload only when the user actually CHANGES (mount is already lazy-initialized).
  useEffect(() => {
    const previous = keyRef.current;
    if (previous === userId) return;
    keyRef.current = userId;
    if (userId == null) {
      // Logged out — empty the in-memory cart and purge the shared "guest" bucket so the
      // header badge doesn't keep showing the last session's items (the "still shows 80
      // after logout" bug). Each customer's own cart (keyed by phone) is left intact so it
      // returns when they sign back in.
      try {
        window.localStorage.removeItem(storageKey(null));
      } catch {
        /* private mode — ignore */
      }
      setItems([]);
      return;
    }

    if (previous == null) {
      // GUEST → SIGNED IN. Carry what they were holding over instead of replacing it.
      //
      // This is the whole first-order flow: a guest taps ADD, the name+mobile sheet appears,
      // and the item is added the moment they're signed in. Loading the customer's stored cart
      // here discarded that item — the sheet said "Signed in", the cart stayed empty, and
      // "Place order" then answered "Add items first". Adding several items first and signing
      // in at checkout lost all of them the same way.
      setItems((guest) => mergeGuestCart(guest, readCart(userId)));
      try {
        window.localStorage.removeItem(storageKey(null)); // it belongs to them now
      } catch {
        /* private mode — ignore */
      }
      return;
    }

    // A DIFFERENT customer signed in on this device — their own cart, not the last one's.
    setItems(readCart(userId));
  }, [userId]);

  // Persist cart changes to the user the items belong to (keyRef, not userId).
  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey(keyRef.current), JSON.stringify(items));
    } catch {
      /* private mode — ignore */
    }
  }, [items]);

  const add = useCallback((item: CartItemInput, qty = 1) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.id === item.id);
      if (existing) {
        return prev.map((i) =>
          i.id === item.id ? { ...i, qty: i.qty + qty, note: item.note ?? i.note } : i
        );
      }
      return [...prev, { ...item, qty }];
    });
  }, []);

  const inc = useCallback((id: number) => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, qty: i.qty + 1 } : i)));
  }, []);

  const dec = useCallback((id: number) => {
    setItems((prev) =>
      prev
        .map((i) => (i.id === id ? { ...i, qty: i.qty - 1 } : i))
        .filter((i) => i.qty > 0)
    );
  }, []);

  const remove = useCallback((id: number) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const openCart = useCallback(() => setIsOpen(true), []);
  const closeCart = useCallback(() => setIsOpen(false), []);
  const toggleCart = useCallback(() => setIsOpen((o) => !o), []);

  const value = useMemo<CartContextValue>(() => {
    const count = items.reduce((n, i) => n + i.qty, 0);
    const subTotal = items.reduce((s, i) => s + i.qty * i.price, 0);
    return {
      items,
      count,
      subTotal,
      qtyOf: (id) => items.find((i) => i.id === id)?.qty ?? 0,
      add,
      inc,
      dec,
      remove,
      clear,
      isOpen,
      openCart,
      closeCart,
      toggleCart
    };
  }, [items, add, inc, dec, remove, clear, isOpen, openCart, closeCart, toggleCart]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within a <CartProvider>');
  return ctx;
}
